import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdditionalTech from './AdditionalTech';
import { add } from 'date-fns';

const TYPE_META = {
  service:    { label: 'Service',     badge: 'bg-blue-100 text-blue-800 border-blue-300' },
  inspection: { label: 'Inspection',  badge: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  wip:        { label: 'WIP',         badge: 'bg-purple-100 text-purple-800 border-purple-300' },
  operations: { label: 'Operations',  badge: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
};

function classNames(...xs) { return xs.filter(Boolean).join(' '); }

// function toISO(dateLike) {
//   const d = new Date(dateLike);
//   const y = d.getFullYear();
//   const m = String(d.getMonth() + 1).padStart(2, '0');
//   const day = String(d.getDate()).padStart(2, '0');
//   return `${y}-${m}-${day}`;
// }
function toLocalISO(dateLike) {
  const d = new Date(dateLike);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`; // purely local, no UTC shift
}

function formatTime(t) {
  if (!t) return '';
  const [hh, mm = '00'] = t.split(':');
  const h = parseInt(hh || '0', 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${mm.padStart(2, '0')} ${ampm}`;
}

function groupByDate(items) {
  const map = {};
  for (const it of items) {
    (map[it._date] ||= []).push(it);
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, rows]) => ({ date, rows: rows.sort((x, y) => (x.start_time || '').localeCompare(y.start_time || '')) }));
}

const TechSchedules = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const todayISO = toLocalISO(new Date());
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState([]); // all combined items
  const [filters, setFilters] = useState({
    service: true,
    inspection: true,
    wip: true,
    operations: true,
  });
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(todayISO);
  const [toDate, setToDate] = useState(todayISO);

  const toggleFilter = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  const [userFName, setUserFName] = useState(null);

  // for the expand and collapse functionality
  const [showFullNotes, setShowFullNotes] = useState(false);
  const notes = selected?.meta?.coordinator_comment || selected?.notes || '—';
  const needsTruncateNotes = notes.length > 200;
  const visibleNotes = needsTruncateNotes && !showFullNotes
    ? notes.slice(0, 200) + '…'
    : notes;
  // for the expand and collapse functionality

  function parseLocalYMD(ymd) {
  // Treat 'YYYY-MM-DD' as a local date, not UTC
  if (typeof ymd === 'string') {
    const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return null;
  }

  function toLocalISO(dateLike) {
    // If already a pure Y-M-D, keep it as-is (no shift)
    if (typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) {
      return dateLike;
    }
    // If string with time like 'YYYY-MM-DDTHH:mm', it parses as **local**, so that's fine
    let d = null;
    if (typeof dateLike === 'string') {
      d = parseLocalYMD(dateLike) || new Date(dateLike);
    } else {
      d = new Date(dateLike);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function formatLocalYMDLabel(ymd) {
    // Pretty label without UTC surprises
    const d = parseLocalYMD(ymd) || new Date(); // fallback
    return d.toLocaleDateString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
    });
  }

  const firstYMD = (v) => (typeof v === 'string' ? v.slice(0, 10) : v);

  //helper for range filter
  function eachDayInclusive(fromISO, toISO) {
    // fromISO/toISO are "YYYY-MM-DD"
    if (!fromISO && !toISO) return [];
    const start = parseLocalYMD(fromISO || toISO);
    const end   = parseLocalYMD(toISO   || fromISO);
    if (!start || !end) return [];
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      days.push(`${y}-${m}-${day}`); // local Y-M-D
    }
    return days;
  }

  // ------- Normalizers for each endpoint shape -------
  const normalizeService = (arr) =>
  (Array.isArray(arr) ? arr : []).map((log) => {
    const date = firstYMD(log.SCHEDULE_DATE || log.DATE || todayISO);
    return {
      _type: 'service',
      id: log.id ?? log.ID,
      _date: toLocalISO(date),          // -> "2025-09-13"
      start_time: log.TIME || null,
      end_time:  null,   // 👈 your payload has "end_time"
      job_site: log.ADDRESS || '',
      customer: log.CUSTOMER || '',
      status: (log.STATUS || '').toLowerCase(), // "assigned"
        notes: log.NOTES || '',
      meta: {
        called_by: log.CALLED_BY || '',
        phone: log.PHONE || '',
        tech: log.TECH || '',
      },
      serv_tech_name: log.TECH || '',
      TROUBLE: log.TROUBLE || '',
      additional_tech: Array.isArray(log.additional_tech) ? log.additional_tech : [],
      _canceled: (log.STATUS || '').toLowerCase().includes('cancel'),
    };
  });

  const normalizeInspection = (arr) => {
    const input = Array.isArray(arr) ? arr : [];
    const out = [];

    for (const log of input) {
      // Determine range: prefer explicit from/to, fall back to a single date
      const fromISO = toLocalISO(
        log.schedule_date_from || log.scheduled_due_date || log.schedule_date_to || todayISO
      );
      const toISO = toLocalISO(
        log.schedule_date_to || log.scheduled_due_date || log.schedule_date_from || todayISO
      );

      const days = eachDayInclusive(fromISO, toISO);

      for (const day of days) {
        out.push({
          _type: 'inspection',
          id: log.inspection_id,
          _date: day, // one row per calendar day
          start_time: log.schedule_time_in || null,
          end_time: log.schedule_time_out || null, // if you have it; otherwise null
          job_site: [log.job_site_address, log.job_site_city, log.job_site_state, log.job_site_zip]
            .filter(Boolean)
            .join(', '),
          customer: log.customer_name || '',
          status: (log.inspection_status || '').toLowerCase(),
          notes: log.inspection_notes || log.notes || '',
          meta: {
            contact_name: log.contact_name,
            contact_phone: log.contact_phone,
            contact_email: log.contact_email,
            contact_list: Array.isArray(log.contact_list) ? log.contact_list : [],
            // keep original span so the modal can show it
            schedule_from: fromISO,
            schedule_to: toISO,
          },
          tech_name: log.tech_name || '',
          additional_tech: Array.isArray(log.additional_tech) ? log.additional_tech : [],
          _canceled: (log.inspection_status || '').toLowerCase().includes('cancel'),
        });
      }
    }

    return out;
  };

  const normalizeWip = (arr) => {
    const input = Array.isArray(arr) ? arr : [];
    const out = [];
    for (const wip of input) {
      const fromISO = toLocalISO(wip.schedule_from || wip.schedule_to || todayISO);
      const toISO   = toLocalISO(wip.schedule_to   || wip.schedule_from || todayISO);

      const days = eachDayInclusive(fromISO, toISO);
      for (const day of days) {
        out.push({
          _type: 'wip',
          id: wip.schedule_id,
          _date: day,                                 // 👈 one row per calendar day
          start_time: wip.start_time || null,
          end_time: null,
          job_site: wip.job_site || '',
          customer: wip.customer_name || '',
          status: (wip.status || '').toLowerCase(),
          notes: wip.job_description || '',
          meta: {
            job_number: wip.job_number,
            type: wip.type,
            coordinator_comment: wip.user_comment,
            // keep original range if you want to show it in the modal:
            schedule_from: fromISO,
            schedule_to: toISO,
          },
          links: wip.links || [],
          user: wip.user || '',
          tech_name: wip.tech_name || '',
          additional_tech: wip.additional_tech || '',
          _canceled: (wip.status || '').toLowerCase().includes('cancel'),
        });
      }
    }
    return out;
  };

  const normalizeOps = (arr) =>
    (Array.isArray(arr) ? arr : []).map((op) => {
      const date = op.schedule_from || op.date || todayISO;
      return {
        _type: 'operations',
        id: op.id,
        _date: toLocalISO(date),
        start_time: op.start_time || null,
        end_time: null,
        job_site: op.job_site || '',       // if you later join address, it’ll show here
        customer: op.customer_name || '',  // optional if you later join
        status: (op.status || '').toLowerCase(), // 'scheduled', 'in progress', etc.
        notes: op.description || '',       // <-- your new column
        meta: {
          tech_name: op.tech_name || '',
          time_log: op.time_log || '',
          coordinator_comment: op.user_comment || '',
          raw_date: op.date || null,
        },
        _canceled: (op.status || '').toLowerCase().includes('cancel'),
      };
    });

  // ------- Fetch all four endpoints -------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        serviceRes,
        inspRes,
        wipRes,
        opsRes,
      ] = await Promise.all([
        axios.get(`${apiUrl}/service_crud.php?getTechList=true`),
        axios.get(`${apiUrl}/contracts_crud.php?getTechList=true`),
        axios.get(`${apiUrl}/wip_crud.php?getWipTechList=true`),
        axios.get(`${apiUrl}/operation_crud.php?getOpTechList=1`),
      ]);
      
      //  console.log({serviceRes}, {inspRes}, {wipRes}, {opsRes});
      // Service: ['success', array]
      let serviceData = [];
      if (Array.isArray(serviceRes.data[1])) {
        if (serviceRes?.data[0] === 'success' && Array.isArray(serviceRes?.data[1])) {
          setUserFName(serviceRes.data[3] || null);
          serviceData = serviceRes.data[1];
        }
      }
      // Inspection: { success: array }
      const inspectionData = Array.isArray(inspRes.data?.success)
        ? inspRes.data.success
        : [];

      // WIP: ['success', array]
      const wipData =
        Array.isArray(wipRes.data) && wipRes.data[0] === 'success'
          ? wipRes.data[1]
          : [];

      // Ops: either ['success', array] OR { success: true, data: [] }
      let opsData = [];
      if (Array.isArray(opsRes.data) && opsRes.data[0] === 'success') {
        opsData = Array.isArray(opsRes.data[1]) ? opsRes.data[1] : [];
      } else if (opsRes.data?.success && Array.isArray(opsRes.data.data)) {
        opsData = opsRes.data.data;
      }

      const merged = [
        ...normalizeService(serviceData),
        ...normalizeInspection(inspectionData),
        ...normalizeWip(wipData),
        ...normalizeOps(opsData),
      ];

      setRaw(merged);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login');
      } else if (err?.response?.status === 403) {
        toast.error("Access denied.", { position: 'top-center' });
      } else {
        toast.error("Error loading schedules.", { position: 'top-center' });
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ------- Filter + Search + Range -------
  const filtered = useMemo(() => {
    let a = fromDate, b = toDate;
    if (a && b && a > b) [a, b] = [b, a];          // swap if reversed
    const inRange = (d) => (!a || d >= a) && (!b || d <= b);
    const q = (search || '').toLowerCase();

    return raw
      .filter(it => filters[it._type])
      .filter(it => inRange(it._date))             // compares "YYYY-MM-DD" strings
      .filter(it =>
        !q ||
        (it.job_site || '').toLowerCase().includes(q) ||
        (it.customer || '').toLowerCase().includes(q) ||
        (it.notes || '').toLowerCase().includes(q)
      );
  }, [raw, filters, search, fromDate, toDate]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  const link = (ev) => {
    setSelected(ev);
    if(ev._type === 'service') {
      // navigate(`/t/tservdetail/${ev.id}`);
      handleActivation(ev.id, ev.status, ev.meta?.tech);
    } else if(ev._type === 'inspection') {
      navigate(`/t/tinspdetail/${ev.id}`);
    } else if(ev._type === 'wip') {
      navigate(`/t/twipdetails/${ev.id}`);
    } else if(ev._type === 'operation') {
      // navigate(`/t/toperationdetails/${ev.id}`);
    }
  };

  const handleActivation = async(id, status, techName) => {
    if(userFName && techName && userFName.toLowerCase() !== techName.toLowerCase()){
      toast.info("You are not authorized to activate /or see details of this call.", { position: "top-center" });
    return;
    }    
    const bodyData = {
      status: "activateCall",
      id: id
    };
    if(status.toLowerCase() === 'activated')
    {
      navigate(`/t/tservdetail/${id}`);
    }
    else
    {
      try {  
        const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
          headers: { 'Content-Type': 'application/json' },
        });    
        if (response.data[0] === 'success') 
        {
          navigate(`/t/tservdetail/${id}`);
        } 
        else 
        {
          toast.info("Failed to activate call.", { position: "top-center" });
        }
      } 
      catch (error) 
      {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! You don't have access to this area. Speak to the CFO.", {
            position: "top-center",
          });
        }
      } 
      finally 
      {
        setLoading(false);
      }      
    }
  }  

  return (
    <div className="mx-auto max-w-[1000px] p-4">
      <h1 className="text-2xl font-bold mb-3">Tech Schedules</h1>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search address / customer / notes…"
          className="border rounded px-3 py-2 md:col-span-2"
        />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="border rounded px-3 py-2"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="border rounded px-3 py-2"
        />
      </div>

      {/* Type filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(TYPE_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => toggleFilter(key)}
            className={classNames(
              'px-3 py-1 rounded-full border text-sm',
              filters[key] ? meta.badge : 'bg-white text-gray-500 border-gray-300'
            )}
          >
            {meta.label}
          </button>
        ))}
        <button
          onClick={fetchAll}
          className="ml-auto bg-blue-600 text-white px-3 py-1.5 rounded shadow hover:bg-blue-700"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Date-grouped list */}
      {grouped.length === 0 && !loading && (
        <div className="text-gray-600">No schedules match your filters.</div>
      )}

      <div className="space-y-6">
        {grouped
        .filter(({ rows }) =>
              rows.some(ev => (ev.status || '').toLowerCase() !== 'tentative')
        )
        .map(({ date, rows }) => (
          <section key={date}>
            <div className="text-sm text-gray-500 mb-2">
              {formatLocalYMDLabel(date)}
            </div>

            <ul className="space-y-2">
              {rows
               .map((ev, i) => {
                const meta = TYPE_META[ev._type] || { badge: 'bg-gray-100 text-gray-700 border-gray-300' };
                const canceled = ev._canceled;
                const strike = canceled ? 'line-through text-gray-400' : '';
                return (
                  <li key={`${date}-${ev._type}-${ev.id || i}`} className="bg-white border rounded shadow-sm p-3">
                    <button onClick={() => setSelected(ev)} className="w-full text-left">
                    {/* <button onClick={() => link(ev)} className="w-full text-left"> */}
                      <div className="flex items-start gap-3">
                        {/* Time + badge */}
                        <div className="text-center">
                          <div className={`text-base font-semibold text-xs ${strike}`}>
                            {formatTime(ev.start_time)}{ev.end_time ? `–${formatTime(ev.end_time)}` : ''}
                          </div>
                          <span className={classNames('inline-block mt-1 px-2 py-0.5 text-xs border rounded', meta.badge)}>
                            {TYPE_META[ev._type]?.label || ev._type}
                          </span>
                        </div>

                        {/* Info */}
                        <div className="flex-1">
                          <div className={classNames('font-semibold', strike)}>{ev.job_site || '(No Address)'}</div>
                          <div className={classNames('text-sm text-gray-600', strike)}>
                            {ev.customer || ''}
                          </div>

                          {/* Ops description inline */}
                          {ev._type === 'operations' && ev.notes && (
                            <div className={classNames('text-sm mt-1', strike)}>
                              <span className="font-medium">Description: </span>{ev.notes}
                            </div>
                          )}

                          {!!ev.status && !canceled && (
                            <div className="text-xs text-gray-500 mt-1">Status: {ev.status}</div>
                          )}
                          {canceled && (
                            <div className="text-xs text-red-600 mt-1 font-medium">Canceled</div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      {/* Details modal */}
      {selected && (
        <div className="fixed inset-0 z-50">
          {/* Dim + blur overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />

          {/* Bottom sheet panel */}
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-2xl bg-white/95 backdrop-blur-md shadow-2xl ring-1 ring-gray-200">
            {/* Drag handle */}
            <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-gray-300" />

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-gray-900">Details</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {formatLocalYMDLabel(selected._date)} <br/>
                  {(selected._type === 'wip' || selected._type === 'inspection') && selected.meta?.schedule_from && selected.meta?.schedule_to && (
                    <span className="text-xs text-gray-500">
                      Scheduled: {selected.meta.schedule_from} → {selected.meta.schedule_to}
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">ID:{selected.id}</p>
              </div>

              {/* Status pill */}
              <span
                className={[
                  'ml-3 shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset',
                  selected.status?.includes('complete')
                    ? 'bg-green-50 text-green-700 ring-green-600/20'
                    : selected.status?.includes('cancel')
                    ? 'bg-red-50 text-red-700 ring-red-600/20'
                    : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
                ].join(' ')}
              >
                {selected.status || '—'}
              </span>
            </div>

            {/* Body */}
            <div className="px-4 py-3 space-y-3 max-h-[65vh] overflow-y-auto">
              {/* Type + time row */}
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium
                                bg-gray-50 text-gray-700 border-gray-200">
                  {TYPE_META[selected._type]?.label || selected._type}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatTime(selected.start_time)}
                  {selected.end_time ? ` – ${formatTime(selected.end_time)}` : ''}                  
                </span>
              </div>

              {/* Description of the service call */}
              {selected._type === 'service' && selected.TROUBLE && (
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">PO# / Service Ticket Notes:</dt>
                  <dd className="items-center text-sm font-medium rounded-md bg-gray-50 text-red-700 border-gray-200">
                    {selected.TROUBLE}
                  </dd>
                </div>              
              )}             

              <dl className="grid grid-cols-1 gap-y-2 text-sm">
                {(selected._type === 'wip' || selected._type === 'inspection') && selected.tech_name && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Lead Tech</dt>
                    <dd className="mt-0.5 font-medium text-gray-900">{selected.tech_name}</dd>
                  </div>
                )}
                {selected._type === 'service' && selected.serv_tech_name && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Lead Tech</dt>
                    <dd className="mt-0.5 font-medium text-gray-900">{selected.serv_tech_name}</dd>
                  </div>
                )}

                {/* Additional SERVICE Contacts */}
                {selected._type === 'service' && Array.isArray(selected.additional_tech) && selected.additional_tech.length > 0 && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Additional Tech(s)</dt>
                    <dd className="mt-0.5 font-medium text-gray-900">
                      {selected.additional_tech.map((t, i) => (
                        <div key={i} className="font-medium text-blue-700">{t.tech_name}</div>
                      ))}
                    </dd>
                  </div>
                )}

                {/* Additional inspection Contacts */}
                {selected._type === 'inspection' && Array.isArray(selected.additional_tech) && selected.additional_tech.length > 0 && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Additional Tech(s)</dt>
                    <dd className="mt-0.5 font-medium text-gray-900">
                      {selected.additional_tech.map((t, i) => (
                        <div key={i} className="font-medium text-blue-700">{t.name}</div>
                      ))}
                    </dd>
                  </div>
                )}

                {/* Additional wip Contacts */}
                {selected._type === 'wip' && selected.additional_tech && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Additional Tech(s)</dt>
                    <dd className="mt-0.5 font-medium text-gray-900">
                      <div className="font-medium text-blue-700">{selected?.additional_tech}</div>
                    </dd>
                  </div>
                )}

                {!!selected.user && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Logged by</dt>
                    <dd className="mt-0.5 font-medium text-gray-900">{selected.user}</dd>
                  </div>
                )}

                <div className="rounded-lg bg-gray-50 p-2">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Customer /Address</dt>
                  <dd className="mt-0.5 font-bold text-gray-900">{selected.customer || '—'}</dd>
                  <dd className="mt-0.5 font-medium text-gray-500">{selected.job_site || '—'}</dd>
                </div>

                {/* Operations extra */}
                {selected._type === 'operations' && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Description</dt>
                    <dd className="mt-0.5 text-gray-900">
                      {selected.notes || '—'}
                    </dd>
                  </div>
                )}

                {/* Service extra */}
                {selected._type === 'service' && (
                  <>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Called By</dt>
                      <dd className="mt-0.5 font-medium text-gray-900">{selected.meta?.called_by || '—'}</dd>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Phone</dt>
                      <dd className="mt-0.5 font-medium text-gray-900">{selected.meta?.phone || '—'}</dd>
                    </div>
                  </>
                )}

                {/* Inspection extra */}
                {selected._type === 'inspection' && (
                  <>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Primary Contact</dt>
                      <dd className="mt-0.5 font-medium text-gray-900">{selected.meta?.contact_name || '—'}</dd>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Contact Phone</dt>
                      <dd className="mt-0.5 font-medium text-gray-900">{selected.meta?.contact_phone || '—'}</dd>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Contact Email</dt>
                      <dd className="mt-0.5 font-medium text-gray-900 break-words">{selected.meta?.contact_email || '—'}</dd>
                    </div>
                  </>
                )}

                {/* WIP extra */}
                {selected._type === 'wip' && (
                  <>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Job #</dt>
                      <dd className="mt-0.5 font-bold text-green-900">{selected.meta?.job_number || '—'}</dd>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Type</dt>
                      <dd className="mt-0.5 font-medium text-gray-900">{selected.meta?.type || '—'}</dd>
                    </div>
                  </>
                )}
              </dl>

              {selected.links && selected.links.length > 0 && (
                <div className="rounded-lg bg-gray-100 border border-gray-300 p-2">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                    Related Links
                  </dt>
                  <dd className="mt-0.5 text-gray-900">
                    <ul className="list-disc pl-5">
                          {selected.links.map((l) => (
                            <li key={l.id} className="flex items-center gap-3">
                              <span className="truncate text-sm font-medium text-gray-900">
                                {l.label || 'Untitled'}
                              </span>
                              <span className="text-gray-300">→</span>
                              <a
                                href={
                                  l.link && /^https?:\/\//i.test(l.link) ? l.link : `https://${l.link}`
                                }
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="truncate text-sm text-indigo-600 hover:underline"
                                title={l.link}
                              >
                                {l.link}
                              </a>
                            </li>
                          ))}                      
                    </ul>
                  </dd>
                </div>
              )}
              
              <div className="rounded-lg bg-gray-50 p-2">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Internal Notes
                </dt>
                <dd className="mt-0.5 text-gray-900 whitespace-pre-wrap">
                  {visibleNotes}
                </dd>

                {needsTruncateNotes && (
                  <div className="mt-1">
                    <button
                      type="button"
                      onClick={() => setShowFullNotes(v => !v)}
                      className="text-xs bg-gray-200 p-2 mt-3 font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                      aria-expanded={showFullNotes}
                    >
                      {showFullNotes ? 'Show less' : 'Show more'}
                    </button>
                  </div>
                )}
              </div>

            </div>                  

            {/* Footer */}
            <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-gray-200 bg-white/90 px-4 py-3">
              <button
                onClick={() => setSelected(null)}
                className="w-full rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-200"
              >
                Close
              </button>            
              <button
                onClick={() => link(selected)}
                className="w-full rounded-xl bg-blue-400 px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-200"
              >
                Active
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechSchedules;
